import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import { lazyGetDefaultRibbonControlInMenuRendererFunction } from "owa-acui-lazy";
import { appIcons, getAppStrings } from "owa-acui-theme";
import type { ControlInMenuRendererFunction } from "owa-acui";
import type { IMenuItemStyles } from "@fluentui/react/lib/ContextualMenu";
import { submenuChevronStyle } from "./submenuChevronStyle";

const getEmptyControlInMenuRendererFunction: () => ControlInMenuRendererFunction = () => () => [];

// Vertically center the submenu expand chevron with the item icon and label
// across every ribbon overflow/flyout menu. mergeStyleSets
// overrides (does not stack) these properties downstream, so controls that set
// their own subMenuIcon styles still win with their own values.
const centeredSubMenuChevronStyles: Partial<IMenuItemStyles> = {
    subMenuIcon: submenuChevronStyle,
};

/**
 * Custom hook to lazy load the in-menu renderer function.
 */
export const useControlInMenuRendererFunction = (skipResultMemoization: boolean): ControlInMenuRendererFunction => {
    return useComputedValue(() => {
        const getInMenuRenderer = 
        /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
         * BASELINE. Do not copy and paste!
         *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
        lazyGetDefaultRibbonControlInMenuRendererFunction.tryImportForRender();
        
        // Get the renderer function or fallback to empty renderer function
        const rendererFunction = getInMenuRenderer
            ? getInMenuRenderer(appIcons, getAppStrings(), skipResultMemoization)
            : getEmptyControlInMenuRendererFunction();
        
        // Ensure that we are passing the necessary parameters and invoke the renderer function.
        // Inject the centered submenu chevron styles so the expand arrow lines up
        // with each item's icon and label; existing per-menu styles take precedence.
        return parameters => rendererFunction({
            ...parameters,
            customMenuItemStyles: {
                ...centeredSubMenuChevronStyles,
                ...parameters.customMenuItemStyles,
            },
        });
    }, [skipResultMemoization]);
};
